import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Thermometer, Home, Sun, Battery, Droplets } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MeasureSelectionProps {
  selectedCategory: string;
  selectedType: string;
  onSelect: (category: string, type: string) => void;
}

interface Measure {
  id: string;
  category: string;
  type: string;
  unit: string;
  base_amount: number;
  per_unit_amount: number;
  notes: string;
}

const categoryIcons = {
  warmtepomp: Thermometer,
  isolatie: Home,
  zonneboiler: Droplets,
  zonnepanelen: Sun,
  thuisbatterij: Battery
};

const categoryLabels = {
  warmtepomp: "Warmtepompen",
  isolatie: "Isolatiemaatregelen", 
  zonneboiler: "Zonneboilers",
  zonnepanelen: "Zonnepanelen",
  thuisbatterij: "Thuisbatterijen"
};

const categoryDescriptions = {
  warmtepomp: "Efficiënte verwarming met lage energiekosten",
  isolatie: "Bespaar energie door betere isolatie",
  zonneboiler: "Duurzame warmwater met zonne-energie",
  zonnepanelen: "Eigen stroom opwekken met zonnepanelen",
  thuisbatterij: "Energie opslaan voor later gebruik"
};

export const MeasureSelection = ({ selectedCategory, selectedType, onSelect }: MeasureSelectionProps) => {
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMeasures = async () => {
      try {
        const { data, error } = await supabase
          .from('isde_measures')
          .select('*')
          .eq('is_active', true)
          .order('category', { ascending: true })
          .order('type', { ascending: true });

        if (error) throw error;
        setMeasures(data || []);
      } catch (error) {
        console.error('Error fetching measures:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMeasures();
  }, []);

  const categories = [...new Set(measures.map(m => m.category))];
  const typesForCategory = measures.filter(m => m.category === selectedCategory);

  if (loading) {
    return (
      <div className="grid gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Selecteer uw verduurzamingsmaatregel</h3>
        
        {/* Category Selection */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {categories.map((category) => {
            const Icon = categoryIcons[category as keyof typeof categoryIcons];
            const isSelected = selectedCategory === category;
            
            return (
              <Card 
                key={category}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => onSelect(category, '')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    {categoryLabels[category as keyof typeof categoryLabels]}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {categoryDescriptions[category as keyof typeof categoryDescriptions]}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Type Selection */}
        {selectedCategory && typesForCategory.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Kies het specifieke type:</h4>
            <div className="grid gap-3">
              {typesForCategory.map((measure) => {
                const isSelected = selectedType === measure.type;
                const isNotSubsidizable = measure.base_amount === 0 && measure.per_unit_amount === 0;
                
                return (
                  <Card
                    key={measure.id}
                    className={`cursor-pointer transition-all ${
                      isNotSubsidizable 
                        ? 'opacity-60 cursor-not-allowed' 
                        : isSelected 
                          ? 'ring-2 ring-primary bg-primary/5' 
                          : 'hover:bg-muted/50'
                    }`}
                    onClick={() => !isNotSubsidizable && onSelect(selectedCategory, measure.type)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h5 className="font-medium capitalize">{measure.type}</h5>
                          <p className="text-sm text-muted-foreground">{measure.notes}</p>
                        </div>
                        <div className="text-right">
                          {isNotSubsidizable ? (
                            <Badge variant="destructive">Niet subsidiabel</Badge>
                          ) : (
                            <div>
                              {measure.base_amount > 0 && (
                                <p className="text-sm font-medium">€{measure.base_amount.toFixed(0)} basis</p>
                              )}
                              {measure.per_unit_amount > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  + €{measure.per_unit_amount.toFixed(2)} per {measure.unit}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Warning for non-subsidizable options */}
        {selectedType && typesForCategory.find(m => m.type === selectedType && m.base_amount === 0 && m.per_unit_amount === 0) && (
          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <h5 className="font-medium text-destructive mb-2">Let op: Geen ISDE subsidie</h5>
            <p className="text-sm text-destructive/80">
              {selectedType} komt niet in aanmerking voor ISDE subsidie volgens de huidige regelgeving. 
              Wel kunnen er andere subsidies beschikbaar zijn.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};